import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { localStorageService } from '@/utils/local_storage';
import LocationHelper from '@/utils/helpers/location';
import useHoneybadger from '@/hooks/useHoneybadger';
import { SolutionContext } from './solution.context';

/** Developer note: this context generally shouldn't be used directly.
 *** Instead the `useLocation` hook should always be used because the initial render `useEffect` needs
 *** to know about the `user` and our design of hooks resolves the potential
 *** for cyclical dependencies in contexts.
 *** For places where the `useLocation` hook cannot be used, it's recommended to call the initLocation
 *** method in a `useEffect` with no dependencies
 **/

const LocationContext = React.createContext({});

const LocationProvider = ({ children }) => {
  // undefined = localStorage not yet read (loading in progress)
  // false     = loaded, but no value stored
  // object    = loaded, real location/region value
  const [location, setLocation] = useState(undefined);
  const [region, setRegion] = useState(undefined);
  const [locationIsDefault, setLocationIsDefault] = useState(undefined);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const skipNextAutoDetect = useRef(false);
  const { notifyHoneybadger } = useHoneybadger();
  const { solutionKey } = useContext(SolutionContext);
  const locationHelpers = useMemo(() => new LocationHelper(solutionKey), [solutionKey]);

  // Read from localStorage on mount (client-only). Uses ?? false so that a missing
  // key resolves to false ("not set") rather than staying as undefined.
  useEffect(() => {
    setLocation(localStorageService.get('location') ?? false);
    setRegion(localStorageService.get('region') ?? false);
    setLocationIsDefault(localStorageService.get('locationIsDefault') ?? false);
  }, []);

  // These internal helpers ensure React state and localStorage are always updated together.
  const persistLocation = (val) => {
    if (val) skipNextAutoDetect.current = false; // Clear location on assessment restart (shba)
    setLocation(val);
    localStorageService.set('location', val);
  };
  const persistRegion = (val) => {
    setRegion(val);
    localStorageService.set('region', val);
  };
  const persistLocationIsDefault = (val) => {
    setLocationIsDefault(val);
    localStorageService.set('locationIsDefault', val);
  };

  const isLocationStale = () => {
    return location && (!location.cachedAt || location.cachedAt + locationHelpers.cacheExpiration < Date.now());
  };

  const initLocation = ({ skipStaleRefresh = false } = {}) => {
    // Storage not yet loaded from localStorage — useLocation will retry when it settles.
    if (location === undefined || region === undefined) return;

    if (skipNextAutoDetect.current) {
      return;
    }

    if ((region === false || location === false) && !isDetectingLocation) {
      setIsDetectingLocation(true);
      locationHelpers.getCurrentRegion().then(({ region, usingDefaultRegion }) => {
        persistRegion(region);
        persistLocationIsDefault(usingDefaultRegion);

        // NOTE: Below is a divergence from the Angular handling of Location when only region is known.
        // Angular only sets the location based on region when it wasn't using the default region.
        // This change should be fine but consider this if unexpected behavior appears around whether or not we know the user's actual location.

        // set location based on region
        if (!location || (isLocationStale() && !skipStaleRefresh)) {
          locationHelpers.checkGeolocationPermissions().then((permissionGranted) => {
            if (permissionGranted) {
              locationHelpers
                .getDetectedLocation()
                .then((detectedLocation) => {
                  persistLocation(detectedLocation);
                  setIsDetectingLocation(false);
                })
                .catch((error) => {
                  persistLocation(locationHelpers.getNamedLocation(region));
                  setIsDetectingLocation(false);
                });
            } else {
              persistLocation(locationHelpers.getNamedLocation(region));
              setIsDetectingLocation(false);
            }
          });
        } else {
          setIsDetectingLocation(false);
        }
      });
    } else if (isLocationStale() && !skipStaleRefresh && !isDetectingLocation) {
      setIsDetectingLocation(true);
      locationHelpers.checkGeolocationPermissions().then((permissionGranted) => {
        if (permissionGranted) {
          locationHelpers
            .getDetectedLocation()
            .then((detectedLocation) => {
              persistLocation(detectedLocation);
              setIsDetectingLocation(false);
            })
            .catch((error) => {
              //do nothing on error - keep current location, but also don't throw an error.
              setIsDetectingLocation(false);
            });
        } else {
          setIsDetectingLocation(false);
        }
      });
    }
  };

  useEffect(() => {
    if (location && (typeof location?.lat == 'undefined' || typeof location?.lng == 'undefined')) {
      notifyHoneybadger('React location context lat lng is undefined', {
        message: 'React location context lat lng is undefined',
        component: 'Location.context',
        context: {
          location: location
        }
      });
    }
  }, [location]);

  const updateLocationByDetection = async () => {
    const detectedLocation = await locationHelpers.getDetectedLocation();
    await updateLocation(detectedLocation);
  };

  const updateLocationByLatLng = async (lat, lng) => {
    const newLocation = await locationHelpers.reverseGeocode(lat, lng);
    await updateLocation({ ...newLocation, lat, lng });
  };

  const reverseGeocode = async (lat, lng) => {
    return locationHelpers.reverseGeocode(lat, lng);
  };

  const updateLocationByPlace = async (place) => {
    if (place?.address_components) {
      await updateLocation(locationHelpers.buildLocationFromPlace(place));
    }
    // else: keep current location unchanged
  };

  const updateLocation = async ({ cachedAt, county, lat, locationName, lng, state, region: passedRegion, city, zip }) => {
    const { region, usingDefaultRegion } = passedRegion
      ? { region: passedRegion, usingDefaultRegion: false }
      : await locationHelpers.getRegionByLatLng(lat, lng);
    persistRegion(region);
    persistLocationIsDefault(usingDefaultRegion);
    persistLocation({ county, latitude: lat, lat, lng, locationName, longitude: lng, state, city, zip, cachedAt: cachedAt || Date.now() });
  };

  const clearLocation = () => {
    skipNextAutoDetect.current = true; // Clear location on assessment restart (shba)
    persistLocation(false);
    persistRegion(false);
    persistLocationIsDefault(false);
  };

  const getLocation = () => {
    return location || {};
  };

  const getRegion = () => {
    return region || {};
  };

  const locationKnown = () => {
    return typeof location?.lat !== 'undefined' && typeof location?.lng !== 'undefined';
  };

  return (
    <LocationContext.Provider
      value={{
        locationHelpers,
        locationSolutionKey: solutionKey,
        location: location,
        locationIsDefault: locationIsDefault,
        initLocation: initLocation,
        locationKnown: locationKnown,
        region: region,
        clearLocation: clearLocation,
        getLocation: getLocation,
        getRegion: getRegion,
        reverseGeocode: reverseGeocode,
        updateLocation: updateLocation,
        updateLocationByLatLng: updateLocationByLatLng,
        updateLocationByPlace: updateLocationByPlace,
        updateLocationByDetection: updateLocationByDetection
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export { LocationContext, LocationProvider };
